export const fragmentShader = `
precision mediump float;

uniform sampler2D u_image;
uniform sampler2D u_colorMap;
uniform sampler2D u_alphaMask;
uniform sampler2D u_cellMap;
uniform float u_usePackedCellMap;
uniform float u_hideSource;
uniform float u_sourceLighten;
uniform float u_sourceGrayscale;
uniform vec3 u_bgColor;
uniform vec2 u_resolution;
uniform vec2 u_imageResolution;
uniform float u_gridCols;
uniform float u_gridRows;
uniform float u_gap;
uniform float u_cornerRadius;
uniform float u_useColorMap;
uniform float u_gradientMode;
uniform float u_lineMode;
uniform float u_ditherMode;
uniform vec3 u_dominantColor;
uniform vec3 u_secondaryColor;

varying vec2 v_texCoord;

float colorDist2(vec3 a, vec3 b) {
    vec3 d = a - b;
    return dot(d, d);
}

float hash12(vec2 p) {
    // Cheap deterministic hash (enough for random-ish per-cell selection)
    return fract(dot(p, vec2(0.06711056, 0.00583715)));
}

vec4 getCellDataUnchecked(vec2 cellIdx) {
    vec2 uv = (cellIdx + 0.5) / vec2(u_gridCols, u_gridRows);

    if (u_usePackedCellMap > 0.5) {
        vec4 cellPacked = texture2D(u_cellMap, uv);
        return vec4(cellPacked.rgb, cellPacked.a);
    }

    vec4 color = texture2D(u_colorMap, uv);
    float alpha = texture2D(u_alphaMask, uv).r;
    return vec4(color.rgb, alpha);
}

vec4 getCellData(vec2 cellIdx) {
    if (cellIdx.x < 0.0 || cellIdx.x >= u_gridCols || 
        cellIdx.y < 0.0 || cellIdx.y >= u_gridRows) {
        return vec4(-1.0);
    }

    return getCellDataUnchecked(cellIdx);
}

vec4 adjustSource(vec4 color) {
    if (u_sourceGrayscale > 0.5) {
        float lum = dot(color.rgb, vec3(0.299, 0.587, 0.114));
        color.rgb = vec3(lum);
    }
    float t = clamp(u_sourceLighten, -1.0, 1.0);
    if (t > 0.0) {
        color.rgb = mix(color.rgb, vec3(1.0), t);
    } else if (t < 0.0) {
        color.rgb = mix(color.rgb, vec3(0.0), -t);
    }
    return color;
}

vec4 sourceAt(vec2 uv) {
    if (u_hideSource > 0.5) {
        return vec4(u_bgColor, 1.0);
    }
    return adjustSource(texture2D(u_image, uv));
}

vec4 rawSourceAt(vec2 uv) {
    // Always sample the underlying source texture, ignoring u_hideSource.
    // Note: keep this *unmodified* so source-only adjustments don't affect effects
    // (e.g. line-mode / dither luminance), nor any logic that relies on the true source.
    return texture2D(u_image, uv);
}

float bayer2(vec2 p2) {
    // 2x2 Bayer base matrix:
    // 0 2
    // 3 1
    float x = p2.x; // 0 or 1
    float y = p2.y; // 0 or 1
    float ySel = step(0.5, y);
    // y=0 -> 2*x
    // y=1 -> (x==0?3:1)
    float row1 = mix(3.0, 1.0, x);
    return mix(2.0 * x, row1, ySel);
}

float bayer4(vec2 p) {
    // Branchless 4x4 ordered dither matrix. Returns threshold in [0,1).
    vec2 f = mod(floor(p), 4.0);
    vec2 p0 = mod(f, 2.0);
    vec2 p1 = floor(f * 0.5);
    float v = 4.0 * bayer2(p0) + bayer2(p1);
    return (v + 0.5) / 16.0;
}

float findRowGroupRightX(vec2 cellIndex, vec3 myColor) {
    // Find the right edge of the contiguous same-color (and not-revealed) run in this row.
    // Constant-bound loop for WebGL1 compatibility.
    float x = cellIndex.x;
    for (int i = 0; i < 128; i++) {
        float nx = x + 1.0;
        if (nx >= u_gridCols) break;
        // Bounds are already guarded by nx/u_gridCols, so skip per-fetch bounds branch.
        vec4 d = getCellDataUnchecked(vec2(nx, cellIndex.y));
        if (d.r < -0.5) break;
        if (d.a < 0.5) break;
        if (colorDist2(d.rgb, myColor) > 0.0001) break;
        x = nx;
    }
    return x;
}

void main() {
    vec2 uv = v_texCoord;
    
    vec2 cellSize = vec2(1.0 / u_gridCols, 1.0 / u_gridRows);
    vec2 cellIndex = floor(uv / cellSize);
    vec2 cellCenter = (cellIndex + 0.5) * cellSize;
    vec2 localUV = (uv - cellIndex * cellSize) / cellSize;

    // Current cell data (used for reveal + merging logic)
    vec4 myData = getCellData(cellIndex);
    vec3 myColor = myData.rgb;

    // If alpha says "reveal", show original image
    if (myData.a < 0.5) {
        gl_FragColor = sourceAt(uv);
        return;
    }

    vec2 maskUV = (cellIndex + 0.5) / vec2(u_gridCols, u_gridRows);

    bool useGap = u_gap > 0.001;
    bool useCorner = u_cornerRadius > 0.001;
    bool needEdges = useGap || useCorner;

    // Pixel aspect for cell-local operations (keep circles circular in pixels)
    float cellPxW = u_resolution.x / u_gridCols;
    float cellPxH = u_resolution.y / u_gridRows;
    float aspectRatio = cellPxW / max(1.0, cellPxH);

    bool edgeLeft = false;
    bool edgeRight = false;
    bool edgeTop = false;
    bool edgeBottom = false;

    if (needEdges) {
        // Outer edge if neighbor is missing, revealed, or a different color
        vec4 leftData = getCellData(cellIndex + vec2(-1.0, 0.0));
        edgeLeft = (leftData.r < -0.5) || (leftData.a < 0.5) || (colorDist2(leftData.rgb, myColor) > 0.0001);

        vec4 rightData = getCellData(cellIndex + vec2(1.0, 0.0));
        edgeRight = (rightData.r < -0.5) || (rightData.a < 0.5) || (colorDist2(rightData.rgb, myColor) > 0.0001);

        vec4 topData = getCellData(cellIndex + vec2(0.0, -1.0));
        edgeTop = (topData.r < -0.5) || (topData.a < 0.5) || (colorDist2(topData.rgb, myColor) > 0.0001);

        vec4 bottomData = getCellData(cellIndex + vec2(0.0, 1.0));
        edgeBottom = (bottomData.r < -0.5) || (bottomData.a < 0.5) || (colorDist2(bottomData.rgb, myColor) > 0.0001);
    }
    
    // Check if we're in the gap - use uniform gap based on smaller dimension
    if (useGap) {
        // Make gap uniform in *pixels* (u_gap is defined in cell-width units)
        float gapX = u_gap * 0.5;
        float gapY = u_gap * 0.5 * aspectRatio;
        
        if ((edgeLeft && localUV.x < gapX) || 
            (edgeRight && localUV.x > (1.0 - gapX)) || 
            (edgeTop && localUV.y < gapY) || 
            (edgeBottom && localUV.y > (1.0 - gapY))) {
            // Show original image in gaps
            gl_FragColor = sourceAt(uv);
            return;
        }
    }

    // Rounded corners for blocks (group-aware): only round where this cell corner is an outer corner.
    // u_cornerRadius is in cell-relative units (0..0.5).
    if (useCorner) {
        // Use the same pixel aspect as gaps, and round the *inset* block shape (post-gap),
        // so corners don't look offset when u_gap > 0.
        float gapX = useGap ? (u_gap * 0.5) : 0.0;
        float gapY = useGap ? (u_gap * 0.5 * aspectRatio) : 0.0;

        float insetL = edgeLeft ? gapX : 0.0;
        float insetR = edgeRight ? gapX : 0.0;
        float insetT = edgeTop ? gapY : 0.0;
        float insetB = edgeBottom ? gapY : 0.0;

        float innerW = max(0.0, 1.0 - insetL - insetR);
        float innerH = max(0.0, 1.0 - insetT - insetB);

        // Work in x-units with y scaled by aspectRatio (so circles are circular in pixels)
        float r = clamp(u_cornerRadius, 0.0, 0.5);
        float maxR = min(0.5 * innerW, 0.5 * (innerH / max(0.00001, aspectRatio)));
        r = min(r, maxR);

        // Top-left
        if (edgeLeft && edgeTop) {
            float px = localUV.x - insetL;
            float py = (localUV.y - insetT) / max(0.00001, aspectRatio);
            if (px < r && py < r) {
                vec2 d = vec2(px - r, py - r);
                if (dot(d, d) > r * r) {
                    gl_FragColor = sourceAt(uv);
                    return;
                }
            }
        }

        // Top-right
        if (edgeRight && edgeTop) {
            float px = (insetL + innerW) - localUV.x;
            float py = (localUV.y - insetT) / max(0.00001, aspectRatio);
            if (px < r && py < r) {
                vec2 d = vec2(px - r, py - r);
                if (dot(d, d) > r * r) {
                    gl_FragColor = sourceAt(uv);
                    return;
                }
            }
        }

        // Bottom-left
        if (edgeLeft && edgeBottom) {
            float px = localUV.x - insetL;
            float py = (insetT + innerH - localUV.y) / max(0.00001, aspectRatio);
            if (px < r && py < r) {
                vec2 d = vec2(px - r, py - r);
                if (dot(d, d) > r * r) {
                    gl_FragColor = sourceAt(uv);
                    return;
                }
            }
        }

        // Bottom-right
        if (edgeRight && edgeBottom) {
            float px = (insetL + innerW) - localUV.x;
            float py = (insetT + innerH - localUV.y) / max(0.00001, aspectRatio);
            if (px < r && py < r) {
                vec2 d = vec2(px - r, py - r);
                if (dot(d, d) > r * r) {
                    gl_FragColor = sourceAt(uv);
                    return;
                }
            }
        }
    }

    // Line mode: for each block/group, take the source image column at the block's right edge
    // (within the current row) and stretch it across the full width of that block.
    if (u_lineMode > 0.5) {
        // If we already know there is a right edge, the run length is 0 => skip the scan loop.
        bool lineEdgeRight = edgeRight;
        if (!needEdges) {
            vec4 rightData = getCellData(cellIndex + vec2(1.0, 0.0));
            lineEdgeRight = (rightData.r < -0.5) || (rightData.a < 0.5) || (colorDist2(rightData.rgb, myColor) > 0.0001);
        }

        float rightX = lineEdgeRight ? cellIndex.x : findRowGroupRightX(cellIndex, myColor);
        // Compute the right edge in source pixel coordinates, then sample the rightmost pixel inside.
        float imgW = max(1.0, u_imageResolution.x);
        float edgeUvX = min((rightX + 1.0) * cellSize.x, 1.0);
        float edgePxX = edgeUvX * imgW;

        // Rightmost pixel *inside* the block: floor(edgePxX) - 1.
        float pxX = floor(edgePxX) - 1.0;
        pxX = clamp(pxX, 0.0, imgW - 1.0);

        float sampleX = (pxX + 0.5) / imgW;
        gl_FragColor = rawSourceAt(vec2(sampleX, uv.y));
        return;
    }

    // Dither mode: dither the source luminance behind each pixel using two colors:
    // - the current block color
    // - the most common (dominant) color across the whole grid (unless effectively identical)
    if (u_ditherMode > 0.5) {
        vec3 a = myColor;
        vec3 b = u_dominantColor;

        // If the dominant color equals this cell color, use the second-most-common color instead.
        if (colorDist2(a, b) < 0.000004) {
            b = u_secondaryColor;
        }

        // If we still don't have a distinct pair, just render the cell color.
        if (colorDist2(a, b) < 0.000004) {
            gl_FragColor = vec4(a, 1.0);
            return;
        }

        vec2 imgRes = max(u_imageResolution, vec2(1.0));
        vec2 pxUv = (floor(uv * imgRes) + 0.5) / imgRes;
        float lum = dot(rawSourceAt(pxUv).rgb, vec3(0.299, 0.587, 0.114));
        float t = bayer4(gl_FragCoord.xy);
        vec3 outColor = (lum >= t) ? b : a;
        gl_FragColor = vec4(outColor, 1.0);
        return;
    }

    // Gradient mode: replace some blocks with a gradient between neighboring blocks.
    // Direction is always the longer side of the cell (in pixels).
    if (u_gradientMode > 0.5) {
        // Only apply to some cells ("sometimes")
        float r = hash12(cellIndex);
        if (r < 0.22) {
            bool horizontal = cellPxW >= cellPxH;

            vec4 a;
            vec4 b;
            float t;

            if (horizontal) {
                // Blend from left neighbor to right neighbor
                a = getCellData(cellIndex + vec2(-1.0, 0.0));
                b = getCellData(cellIndex + vec2( 1.0, 0.0));
                t = smoothstep(0.0, 1.0, localUV.x);
            } else {
                // Blend from top neighbor to bottom neighbor
                a = getCellData(cellIndex + vec2(0.0, -1.0));
                b = getCellData(cellIndex + vec2(0.0,  1.0));
                t = smoothstep(0.0, 1.0, localUV.y);
            }

            // Only if both neighbors exist and are not revealed
            if (a.r > -0.5 && b.r > -0.5 && a.a > 0.5 && b.a > 0.5) {
                // Avoid gradients between nearly identical colors
                if (colorDist2(a.rgb, b.rgb) > 0.0004) {
                    gl_FragColor = vec4(mix(a.rgb, b.rgb, t), 1.0);
                    return;
                }
            }
        }
    }
    
    vec4 sampledColor;
    if (u_useColorMap > 0.5) {
        if (u_usePackedCellMap > 0.5) {
            sampledColor = vec4(texture2D(u_cellMap, maskUV).rgb, 1.0);
        } else {
            sampledColor = texture2D(u_colorMap, maskUV);
        }
    } else {
        sampledColor = sourceAt(cellCenter);
    }
    
    gl_FragColor = sampledColor;
}
`;
